import NiceModal from '@ebay/nice-modal-react';
import { Badge, Card, Flex, Group, Stack, Text } from '@mantine/core';
import { IconPhoto, IconPlayerPlay, IconBolt, IconMessageCircle } from '@tabler/icons-react';
import { useMemo } from 'react';

import { InstagramPost } from '../../../../types/instagram-comments';

import { CommentsModal } from './CommentsModal';

type Props = {
  post: InstagramPost;
  pageName: string;
};

const mediaLabels: Record<string, string> = {
  REEL: 'Reels',
  STORY: 'Stories',
  FEED: 'Post',
};

export const PostCard = ({ post, pageName }: Props) => {
  const formattedLastCommentDate = useMemo(() => {
    const date = new Date(post.lastCommentDate);
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }, [post.lastCommentDate]);

  const formattedCreatedDate = useMemo(() => {
    const date = new Date(post.createdAt);
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }, [post.createdAt]);

  const getMediaIcon = (type?: string) => {
    switch (type) {
      case 'REEL':
        return <IconPlayerPlay size={24} color="#FF0080" />;
      case 'STORY':
        return <IconBolt size={24} color="#9B59B6" />;
      case 'FEED':
      default:
        return <IconPhoto size={24} color="#3B82F6" />;
    }
  };

  const handleOpenComments = () => {
    NiceModal.show(CommentsModal, {
      pageId: post.page._id,
      pageName: pageName,
      mediaId: post.mediaId,
      mediaType: post.mediaProductType,
    });
  };

  return (
    <Card
      shadow="sm"
      padding="md"
      radius="md"
      withBorder
      style={{ cursor: 'pointer', transition: 'all 0.2s' }}
      onClick={handleOpenComments}
      sx={(theme) => ({
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows.md,
        },
      })}
    >
      <Stack gap="sm">
        <Flex justify="space-between" align="center">
          <Group gap="sm">
            {getMediaIcon(post.mediaProductType)}
            <div>
              <Text fw={600} size="sm">
                {(post.mediaProductType && mediaLabels[post.mediaProductType]) || 'Post'}
              </Text>
              <Text size="xs" c="dimmed">
                ID: {post.mediaId}
              </Text>
            </div>
          </Group>

          <Badge
            size="lg"
            variant="filled"
            color="blue"
            leftSection={<IconMessageCircle size={14} />}
          >
            {post.commentsCount}
          </Badge>
        </Flex>

        <Flex justify="space-between" align="center">
          <Text size="xs" c="dimmed">
            Last comment at:
          </Text>
          <Text size="xs" c="dimmed">
            {formattedLastCommentDate}
          </Text>
        </Flex>

        <Text size="xs" c="dimmed" ta="right">
          The post has been created at: {formattedCreatedDate}
        </Text>
      </Stack>
    </Card>
  );
};
